'use client';

import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Spinner } from '@shared/icons';
import { buildPlayerTrackerImpressionPayload, PLAYER_TRACKER_IMPRESSION, usePlayerTrackerServiceEvent } from '@shared/tracking';
import { useClientConfig } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type DepositLimitTranslate, getDepositLimitSchemaForDialog } from '../../../DepositLimits/DepositLimitSchema';
import { DepositLimits } from '../../../DepositLimits/DepositLimits';
import { toast } from '../../../Toast/Toast';
import { useDepositLimitsQuery } from '../../../my-account/SaferGambling/hooks/useDepositLimitsQuery';
import type { DepositLimitsResponse, PlayerLimit, PlayerLimitRequest } from '../../../my-account/SaferGambling/types';
import { createPlayerLimitRequest, getDepositLimitPath, getDepositLimitsPath } from '../../../my-account/SaferGambling/utils/playerLimits';
import type { DepositLimitsDialogContentProps, DepositLimitValue, RecurrentDepositLimitPopupResponse } from './types';

const PERIODS = ['MONTH', 'WEEK', 'DAY'] as const;

function getPeriodKey(periodValue: number): (typeof PERIODS)[number] {
  return PERIODS[periodValue] ?? 'MONTH';
}

function validateAmount(value: DepositLimitValue, t: DepositLimitTranslate, depositLimits: DepositLimitsResponse | null): string | null {
  const schema = getDepositLimitSchemaForDialog(t, depositLimits, value.period.value);
  const result = schema.safeParse(value);
  if (result.success) {
    return null;
  }
  const amountError = result.error.issues.find(e => e.path.includes('amount'));
  return amountError?.message ?? result.error.issues[0]?.message ?? null;
}

function DepositLimitsDialog() {
  const { session } = useSession();
  const isLoggedIn = !!session?.profile;
  const hasOpenedThisSessionRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  const { data: popupData, mutate: fetchPopupData } = useApiMutation<RecurrentDepositLimitPopupResponse, void>(
    API_ROUTES.ACCOUNTING.RECURRENT_DEPOSIT_LIMIT_POPUP_WAITING_TIME,
    'POST',
    {
      requestOptions: {
        credentials: true,
        headers: { countryCode: getSiteConfig().REGISTRATION.country },
      },
    }
  );

  useEffect(() => {
    if (!isLoggedIn) {
      hasOpenedThisSessionRef.current = false;
      return;
    }
    fetchPopupData(undefined);
  }, [isLoggedIn, fetchPopupData]);

  useEffect(() => {
    if (!isLoggedIn) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(false);
      hasOpenedThisSessionRef.current = false;
      return;
    }
    if (popupData?.waitingTimeExpired === true && !hasOpenedThisSessionRef.current) {
      hasOpenedThisSessionRef.current = true;
      setIsOpen(true);
    }
  }, [isLoggedIn, popupData?.waitingTimeExpired]);

  if (!isLoggedIn) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        data-testid="deposit-limits-dialog"
        className="flex min-h-0 flex-col max-w-full w-full md:max-w-105 h-dvh md:h-fit md:max-h-[90vh] p-0 shadow-none rounded-none md:rounded-lg [&>button]:hidden z-1000"
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        aria-labelledby="deposit-limits-dialog-title"
      >
        <DepositLimitsDialogContent onClose={() => setIsOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

const DEFAULT_VALUE: DepositLimitValue = {
  period: { label: '', value: 0 },
  amount: '',
};

function DepositLimitsDialogContent({ onClose }: DepositLimitsDialogContentProps) {
  const t = useTranslations('form');
  const tDialog = useTranslations('global_dialogs.deposit_limits_dialog');
  const queryClient = useQueryClient();
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const { currency } = useClientConfig();
  const ptsTrackEvent = usePlayerTrackerServiceEvent();
  const hasTrackedImpressionRef = useRef(false);

  const [value, setValue] = useState<DepositLimitValue>(DEFAULT_VALUE);
  const [amountError, setAmountError] = useState<string | null>(null);

  const { data: depositLimits = null, isLoading: isDepositLimitsLoading } = useDepositLimitsQuery(!!playerId);

  const periodKey = getPeriodKey(value.period.value);
  const hasExistingLimit = Boolean(depositLimits?.limits.find(limit => limit.period === periodKey && (limit.limitValue ?? 0) > 0));

  const putWaitingTimeMutation = useApiMutation<void, void>(API_ROUTES.ACCOUNTING.RECURRENT_DEPOSIT_LIMIT_POPUP_WAITING_TIME, 'PUT', {
    mutationOptions: { onSuccess: onClose },
    requestOptions: { credentials: true },
  });

  const addLimitMutation = useApiMutation<PlayerLimit, PlayerLimitRequest>(getDepositLimitPath(periodKey), hasExistingLimit ? 'PUT' : 'POST', {
    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['api', getDepositLimitsPath()],
        });
        onClose();
      },
      onError: () => {
        toast(tDialog('unknown_error_toast'), { type: 'error' });
      },
    },
    requestOptions: {
      credentials: true,
    },
  });

  const handleDoItLater = () => {
    putWaitingTimeMutation.mutate(undefined);
  };

  const handleApply = () => {
    if (!playerId) return;
    const error = validateAmount(value, t, depositLimits);
    if (error) {
      setAmountError(error);
      return;
    }
    setAmountError(null);
    const num = Number(value.amount);
    addLimitMutation.mutate(createPlayerLimitRequest(num, currency));
  };

  const displayValue = useMemo(
    () => ({
      ...value,
      period: { ...value.period, label: value.period.label || t('buttons.monthly') },
    }),
    [value, t]
  );

  const isBusy = putWaitingTimeMutation.isPending || addLimitMutation.isPending;

  useEffect(() => {
    if (isDepositLimitsLoading) return;
    if (hasTrackedImpressionRef.current) return;

    hasTrackedImpressionRef.current = true;
    ptsTrackEvent(buildPlayerTrackerImpressionPayload(PLAYER_TRACKER_IMPRESSION.DEPOSIT_LIMIT_ANNUAL_REMINDER, session.profile));
  }, [isDepositLimitsLoading, ptsTrackEvent, session.profile]);

  return (
    <div className="flex min-h-0 flex-1 flex-col basis-auto">
      <DialogHeader variant="dialog" className="flex shrink-0 items-center justify-center h-14 px-5 py-3 border-b border-border">
        <DialogTitle id="deposit-limits-dialog-title" className="paragraph-explanatory-strong">
          {tDialog('dialog_title')}
        </DialogTitle>
      </DialogHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-8 md:px-8 md:py-10">
        {isDepositLimitsLoading ? (
          <div className="flex justify-center py-8" aria-label={t('aria.loading')}>
            <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} data-testid="deposit-limits-dialog-loading" />
          </div>
        ) : (
          <DepositLimits
            value={displayValue}
            onPeriodChange={option => {
              const nextValue = { ...value, period: option };
              setValue(nextValue);
              if (amountError) {
                setAmountError(validateAmount(nextValue, t, depositLimits));
              }
            }}
            onAmountChange={amount => {
              setValue(prev => ({ ...prev, amount }));
              setAmountError(null);
            }}
            onAmountBlur={() => {}}
            depositLimits={depositLimits}
            amountError={amountError ?? undefined}
            fieldIdPrefix="depositLimitsDialog"
            onApply={handleApply}
            onDoItLater={handleDoItLater}
            isLoading={isBusy}
            isApiValidating={false}
            showDoItLater
            stepLayout={{
              title: tDialog('title'),
              'data-testid': 'deposit-limits-dialog-content',
              showLogo: true,
              showBackButton: false,
              className: 'p-0 md:p-0',
              dataTrackFeature: 'Log In',
              dataTrackContainer: 'Deposit Limits',
              dataImpression: PLAYER_TRACKER_IMPRESSION.DEPOSIT_LIMIT_ANNUAL_REMINDER,
            }}
          />
        )}
      </div>
    </div>
  );
}

export { DepositLimitsDialog };
